import { Button, Flex } from "@daangn/carotene";
import { IconHorizline3VerticalLine } from "@daangn/react-monochrome-icon";
import { BrowserRegionCookieStorage } from "@karrotmarket-com/persistent-storage/client";
import clsx from "clsx";
import { motion } from "framer-motion";
import { KR_MAIN_FEED_ONELINK } from "~/constants";
import { CommonSearchParamKey, buildRegionParamValue } from "~/core";
import useDetectUserAgent from "~/hooks/useDetectUserAgent";
import { useDisclosure } from "~/hooks/useDisclosure";
import { Position } from "~/hooks/useMoveToApp";
import { useTrackClickButtonMoveToApp } from "~/hooks/useTrackClickButtonMoveToApp";
import { Link, useLocale, useRootLoaderData } from "~/remix-lib";
import type { RootLoaderData } from "~/root";
import { KRQrModal } from "~/services/QrModal";
import { krServiceHomeUrl, serviceNameMap } from "~/site-urls";
import { GtmVariableName } from "~/utils/GtmVariable";
import { makeKeywordLinks } from "~/utils/makeKeywordLinks";
import LocationSelectModal from "../common/LocationSelectModal";

import KarrotLogo from "../../KarrotLogo";
import PageMaxWidthWrapper from "../../PageMaxWidthWrapper";
import { parseNavLinkWithSubLinksList } from "../common/GNBParse";
import { SearchButton } from "../common/SearchButton";
import {
  SearchPopover,
  SearchPopoverAnchor,
  SearchPopoverContent,
  SearchPopoverTrigger,
} from "../common/SearchPopover";
import SelectRegionButton from "../common/SelectRegionButton";
import { useGNBBorder } from "../common/useGNBBorder";
import { useSearchButton } from "../common/useSearchButton";
import { SearchBar } from "../global/SearchBar";
import * as css from "./GlobalNavigationBar.css";
import { HamburgerMenu } from "./GlobalNavigationBar__HamburgerMenu";
import { NavigationMenu } from "./NavigationMenu";
import { SearchForm } from "./SearchForm";

const regionBrowserStorage = BrowserRegionCookieStorage.of();

export type GlobalNavigationBarProps = {
  data: RootLoaderData["__gnb"]["data"];
  query: string | null;
  showNavigationMenu?: boolean;
  isDetailPage?: boolean;
  isIndexPage?: boolean;
};

const GlobalNavigationBar: React.FC<GlobalNavigationBarProps> = ({
  isDetailPage = false,
  isIndexPage = false,
  showNavigationMenu = true,
  ...props
}) => {
  const userAgent = useDetectUserAgent();
  const rootData = useRootLoaderData();
  const { trackClickButtonMoveToApp } = useTrackClickButtonMoveToApp();
  const { t } = useLocale();

  const showBorder = useGNBBorder();
  const showSearchButton = useSearchButton(false);

  const {
    isOpen: isHamburgerMenuOpen,
    setOpenState: setHamburgerMenuOpenState,
  } = useDisclosure();

  const qrModalDisclosure = useDisclosure();

  const keywordLinks = makeKeywordLinks({
    pathname: krServiceHomeUrl[serviceNameMap[props.data.serviceName]],
    region: rootData.region,
    keywordRecord: props.data.keywords,
  });

  const searchBarSize = isIndexPage ? "large" : "medium";

  const clickDownloadButton = () => {
    trackClickButtonMoveToApp({
      position: Position.GNB,
    });
    if (userAgent.isMobile) {
      window.open(KR_MAIN_FEED_ONELINK);
      return;
    }

    qrModalDisclosure.onOpen();
  };

  const parsedNavLinkWithSubLinksList = parseNavLinkWithSubLinksList(
    props.data.GNBSubLink?.NavLinkWithSubLinks,
  );

  return (
    <>
      <div className={css.container} data-border={showBorder}>
        <PageMaxWidthWrapper>
          <div className={css.top}>
            <Link
              data-gtm={GtmVariableName.GnbLogo}
              href={`/kr/?${CommonSearchParamKey.In}=${buildRegionParamValue({ region: rootData.region })}`}
              className={css.logo}
              aria-label="당근"
            >
              <KarrotLogo location="gnb" />
            </Link>
            {showNavigationMenu ? (
              <NavigationMenu
                linkWithSubLinks={parsedNavLinkWithSubLinksList}
                className={css.desktopOnly}
              />
            ) : null}
            <Flex
              alignItems="center"
              gap={4}
              display={{ base: "none", medium: "flex" }}
            >
              <SearchPopover>
                <SearchPopoverTrigger asChild>
                  <SearchButton
                    visible={isDetailPage || showSearchButton}
                    label="검색"
                    className={css.searchPopoverTriggerSelector}
                  />
                </SearchPopoverTrigger>
                <SearchPopoverAnchor className={css.searchPopoverAnchor} />
                <SearchPopoverContent
                  className={css.searchPopoverContent}
                  hideWhenDetached
                >
                  <SearchBar
                    region={rootData.region}
                    keywordType={props.data.keywords.type}
                    keywordComponentType={isIndexPage ? "text" : "chip"}
                    keywords={keywordLinks}
                    size={searchBarSize}
                    searchForm={
                      <SearchForm
                        region={rootData.region}
                        query={props.query}
                        searchAutoFocus={true}
                        size={searchBarSize}
                      />
                    }
                    onSaveRegionToStorage={regionBrowserStorage.update}
                  />
                </SearchPopoverContent>
              </SearchPopover>
              <Button
                size="medium"
                variant="brand"
                data-gtm={GtmVariableName.GnbDownload}
                onClick={clickDownloadButton}
              >
                {t("GlobalNavigationBar.app_download_button_label")}
              </Button>
            </Flex>
            <div className={clsx(css.topRightActionButton, css.mobileOnly)}>
              <Flex gap={3} alignItems="center">
                <motion.div
                  initial={false}
                  animate={{
                    x: isDetailPage || showSearchButton ? 0 : 44,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                >
                  <LocationSelectModal
                    trigger={
                      <SelectRegionButton
                        size="small"
                        currentRegionName={rootData.region.name}
                      />
                    }
                    onSaveRegionToStorage={regionBrowserStorage.update}
                  />
                </motion.div>
                <SearchPopover>
                  <SearchPopoverTrigger asChild>
                    <SearchButton
                      visible={isDetailPage || showSearchButton}
                      label="검색"
                      className={css.searchPopoverTriggerSelector}
                    />
                  </SearchPopoverTrigger>
                  <SearchPopoverAnchor className={css.searchPopoverAnchor} />
                  <SearchPopoverContent
                    className={css.searchPopoverContent}
                    hideWhenDetached
                  >
                    <SearchBar
                      region={rootData.region}
                      keywordType={props.data.keywords.type}
                      keywordComponentType={isIndexPage ? "text" : "chip"}
                      keywords={keywordLinks}
                      size={searchBarSize}
                      searchForm={
                        <SearchForm
                          region={rootData.region}
                          query={props.query}
                          searchAutoFocus={true}
                          size={searchBarSize}
                        />
                      }
                      onSaveRegionToStorage={regionBrowserStorage.update}
                    />
                  </SearchPopoverContent>
                </SearchPopover>
              </Flex>
              <div className={css.topRightHamburgerContainer}>
                <HamburgerMenu.Root
                  open={isHamburgerMenuOpen}
                  onOpenChange={setHamburgerMenuOpenState}
                >
                  <HamburgerMenu.Trigger className={css.hamburgerButton}>
                    <div
                      data-gtm={GtmVariableName.GnbHamburger}
                      className={css.hamburgerButtonIcon}
                    >
                      <IconHorizline3VerticalLine size={24} />
                    </div>
                  </HamburgerMenu.Trigger>
                  <HamburgerMenu.Content
                    navLinks={parsedNavLinkWithSubLinksList}
                    appDownloadButtonLabel={t(
                      "GlobalNavigationBar.app_download_button_label",
                    )}
                    appDownloadCaptionLabel={t(
                      "GlobalNavigationBar.app_download_caption_label",
                    )}
                    moreSubLinkButtonLabel={t(
                      "GlobalNavigationBar.see_more_button_label",
                    )}
                    onClickAppDownloadButton={clickDownloadButton}
                  />
                </HamburgerMenu.Root>
              </div>
            </div>
          </div>
        </PageMaxWidthWrapper>
      </div>
      <KRQrModal
        open={qrModalDisclosure.isOpen}
        onOpenChange={qrModalDisclosure.setOpenState}
        qrLink={KR_MAIN_FEED_ONELINK}
      />
    </>
  );
};

export default GlobalNavigationBar;
